import { API_BASE_URL } from "@/src/services/api";
const defaultHeaders = {
  "Content-Type": "application/json"
};
const sanitizeApiBaseUrl = raw => String(raw || "").replace(/\/+$/, "");
const ensureApiBaseUrl = () => {
  const base = sanitizeApiBaseUrl(API_BASE_URL);
  if (!base) {
    throw new Error("API base URL is not configured. Set NEXT_PUBLIC_API_BASE_URL to your backend (e.g. http://localhost:3001).");
  }
  // Catch a common misconfiguration where the API base is set to a relative path.
  if (base.startsWith("/")) {
    throw new Error("API base URL must be an absolute URL (e.g. http://localhost:3001). Set NEXT_PUBLIC_API_BASE_URL.");
  }
  return base;
};
const buildUrl = path => {
  const base = ensureApiBaseUrl();
  if (!path.startsWith("/")) {
    return `${base}/${path}`.replace(/\/+/g, "/");
  }
  return `${base}${path}`;
};
function extractErrorMessage(error) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  try {
    return JSON.stringify(error);
  } catch {
    return "Unexpected error";
  }
}
async function handleResponse(response) {
  if (response.status === 204) {
    return undefined;
  }
  const text = await response.text();
  let payload = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      const hint = process.env.NODE_ENV !== "production" ? " (check NEXT_PUBLIC_API_BASE_URL points to the backend API)" : "";
      throw new Error(`Unexpected non-JSON response from API${hint}.`);
    }
  }
  if (!response.ok) {
    const message = payload && payload?.message || response.statusText || "Request failed";
    throw new Error(message);
  }
  return payload;
}
async function request(path, options) {
  const {
    method = "GET",
    data,
    headers,
    signal
  } = options || {};
  const url = buildUrl(path);
  const init = {
    method,
    headers: {
      ...defaultHeaders,
      ...headers
    },
    signal
  };
  if (data !== undefined) {
    if ((init.headers || {})["Content-Type"] === "application/json") {
      init.body = JSON.stringify(data);
    } else {
      init.body = data;
    }
  }
  try {
    const response = await fetch(url, init);
    return await handleResponse(response);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}
export const restClient = {
  get: (path, options) => request(path, {
    ...(options || {}),
    method: "GET"
  }),
  post: (path, data, options) => request(path, {
    ...(options || {}),
    method: "POST",
    data
  }),
  patch: (path, data, options) => request(path, {
    ...(options || {}),
    method: "PATCH",
    data
  }),
  put: (path, data, options) => request(path, {
    ...(options || {}),
    method: "PUT",
    data
  }),
  delete: (path, options) => request(path, {
    ...(options || {}),
    method: "DELETE"
  })
};